import React, { createContext, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { getSourceExercises, getMuscles } from '../api';
import { setExercises, setMuscles } from '../appState/stateActions';
import { useErrorContext } from './errorProvider';
import { AppState } from '../appState/appInitialState';

import type { Exercise, Muscle } from '../types';

export interface GlobalActionContextProps {
  loadSessionExercises: () => Promise<Record<string, Exercise> | null>;
  loadSessionMuscles: () => Promise<Record<string, Muscle> | null>;
  upsertSessionExercise: (exercise: Exercise) => void;
  removeSessionExercise: (exerciseId: string) => void;
}

const GlobalActionContext = createContext<GlobalActionContextProps>({
  loadSessionExercises: () => Promise.resolve(null),
  loadSessionMuscles: () => Promise.resolve(null),
  upsertSessionExercise: () => { },
  removeSessionExercise: () => { },
} as GlobalActionContextProps);

export const GlobalActionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const dispatch = useDispatch();
  const { setGlobalError } = useErrorContext();
  const exercises = useSelector((state: AppState) => state.exercises);

  const setSessionExercises = (exercises: Record<string, Exercise>) => {
    dispatch(setExercises(exercises));
  };

  const setSessionMuscles = (muscles: Record<string, Muscle>) => {
    dispatch(setMuscles(muscles));
  };

  const loadSessionExercises = async (): Promise<Record<
    string,
    Exercise
  > | null> => {
    const { data, success, error } = await getSourceExercises();
    if (success) {
      const exercises = _.keyBy(data, 'id');
      setSessionExercises(exercises);
      return exercises;
    } else {
      setGlobalError(error?.message || 'Error fetching exercises');
      return null;
    }
  };

  const loadSessionMuscles = async (): Promise<Record<
    string,
    Muscle
  > | null> => {
    const { data, success, error } = await getMuscles();
    if (success) {
      const muscles = _.keyBy(data, 'id');
      setSessionMuscles(muscles);
      return muscles;
    } else {
      setGlobalError(error?.message || 'Error fetching muscles');
      return null;
    }
  };

  const upsertSessionExercise = (exercise: Exercise): void => {
    if (!exercise?.id) {
      return;
    }
    setSessionExercises({
      ...exercises,
      [exercise.id]: exercise,
    });
  };

  const removeSessionExercise = (exerciseId: string): void => {
    const exerciseToUpdate = exercises?.[exerciseId];
    if (!exerciseId || !exerciseToUpdate) {
      return;
    }
    setSessionExercises({
      ...exercises,
      [exerciseId]: {
        ...exerciseToUpdate,
        isDeleted: true,
      },
    });
  };

  return (
    <GlobalActionContext.Provider
      value={{
        loadSessionExercises,
        loadSessionMuscles,
        upsertSessionExercise,
        removeSessionExercise,
      }}
    >
      {children}
    </GlobalActionContext.Provider>
  );
};

export const useGlobalAction = () => {
  return useContext(GlobalActionContext);
};
